import { Table, Typography , Space } from "antd";
import { SearchType } from "../Interfaces/SearchInterfaces";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import ViewButton from "../UI/ViewButton/viewbutton";
import { useNavigate } from "react-router";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { UseMutateAsyncFunction } from "react-query";
import { AxiosResponse } from "axios";
import { getWarehouseInventories } from "../../api/inventorysummary";

const { Text } = Typography;

interface inventoryProps {
  paginatedInventories: any;
  isInventoriesLoading: boolean;
  selectedWarehouse: null;
  setPaginatedInventories: Dispatch<any>;
  InventoriesMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      isActive?: boolean | undefined;
    },
    unknown
  >;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  filter: boolean | undefined;
  search: string;
  tableKey: number;
}

const InventorySummarList = ({
  isInventoriesLoading,
  paginatedInventories,
  selectedWarehouse,
  setPaginatedInventories,
  InventoriesMutation,
  page,
  setPage,
  filter,
  search,
  tableKey,
}: inventoryProps) => {
  const [t] = useTranslation("common");
  const navigate = useNavigate();
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);
  const [totalRows, setTotalRows] = useState<number>(0);

  let totalCost = 0;
  let totalSales = 0;
  
  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      InventoriesMutation({
        limit: page * 10 + 10,
        q: search,
        isActive: filter,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);
        setPaginatedInventories(res.data?.data);
        setTotalRows(res.data?.total);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const [filteredWarehouse, setFilteredWarehouse] = useState([]);
  const [isFilter, setIsFilter] = useState(false);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsFilter(true);
        if (selectedWarehouse) {
          const res = await getWarehouseInventories(selectedWarehouse);
          setFilteredWarehouse(res.data.data);
        } else {
          setFilteredWarehouse(paginatedInventories);
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        setIsFilter(false);
      }
    };
    fetchData();
  }, [selectedWarehouse, paginatedInventories]);

  const columns: ColumnsType<SearchType> = [
    {
      title: `${t("category")}`,
      dataIndex: "item",
      key: "category",
      className: "table-header",
      width: "10rem",
      render: (record: any) => {
        return record ? record.category.name : null;
      },
    },
    {
      title: `${t("item")}`,
      dataIndex: "item",
      key: "item",
      className: "table-header",
      align: "center",
      render: (record: any) => {
        return record ? record.name : null;
      },
    },
    {
      title: `${t("quantity")}`,
      dataIndex: "quantity",
      key: "quantity",
      width: "5rem",
      align: "center"
    },
    {
      title: `${t("average-cost")}`,
      dataIndex: "cost",
      key: "cost",
      width: "7rem",
      align: "center",
      render: (value) => (value ? parseFloat(value).toFixed(2) : "0"),
    },
    {
      title: `${t("average-sales")}`,
      dataIndex: "sales",
      key: "sales",
      width: "7rem",
      align: "center",
      render: (value) => (value ? parseFloat(value).toFixed(2) : "0"),
    },
    {
      title: `${t("totalt-purchase")}`,
      dataIndex: "totalPU",
      key: "totalPU",
      width: "8rem",
      align: "center",
      render: (value) => (value ? parseFloat(value).toFixed(2) : "0"),
    }, 
    {
      title: `${t("total-sales")}`,
      dataIndex: "totalSA",
      key: "totalSA",
      width: "8rem",
      align: "center",
      render: (value) => (value ? parseFloat(value).toFixed(2) : "0"),
    },   
    {
      title: `${t("action")}`,
      key: "action",
      fixed: "right",
      width: "5rem",
      align: "center" as "center",
      render: (record: any) => (
        <Space size="middle">
          <ViewButton
            onClick={() =>
              navigate(`/inventory/summary/details/${record?.id}`, {
                state: {
                  q: record.item.name,
                },
              })
            }
          />
        </Space>
      ),
    },
  ];
  return (
    <>
      <Table
        key={tableKey}
        scroll={{ x: 1000 }}
        loading={isFilter || isInventoriesLoading}
        columns={columns}
        dataSource={
          selectedWarehouse ? filteredWarehouse : paginatedInventories
        }
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
        rowKey={(record: any) => record.id}
        summary={pageData => {
          pageData.forEach(({ totalPU, totalSA }) => {
            totalCost += totalPU;
            totalSales += totalSA;
          });
          
          if(page*10 > totalRows) {
           return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                <Text type="danger" strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                <Table.Summary.Cell index={2}></Table.Summary.Cell>
                <Table.Summary.Cell index={3}></Table.Summary.Cell>
                <Table.Summary.Cell index={4}></Table.Summary.Cell>
                <Table.Summary.Cell index={5}>
                <Text type="success" strong>{totalCost ? totalCost.toFixed(2) : "0"}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                <Text type="success" strong>{totalSales ? totalSales.toFixed(2) : "0"}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );           
          }
        }}
      />
    </>
  );
};

export default InventorySummarList;
